import ThumbDownAltIcon from "@mui/icons-material/ThumbDownAlt";
import ThumbUpAltIcon from "@mui/icons-material/ThumbUpAlt";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material/styles";
import * as React from "react";

import {
  ResourceView,
  useResourceView,
  type ActiveFiltersState,
  type Filter,
  type FilterDefinition,
} from "@/shared/components/ResourceView";
import { useAdminFeedback } from "@/shared/domain/admin/hooks/useAdminFeedback";
import {
  type AgentFeedbackRecord,
  FEEDBACK_CATEGORY_LABELS,
  FeedbackCategory,
  FeedbackSentiment,
} from "@/shared/domain/agentThread";
import {
  ColumnVisibility,
  DataTable,
  cellStyles,
  colId,
  createColumnHelper,
  type ColDef,
  type ColumnPickerOption,
  type DataTableLoadingState,
  type ICellRendererParams,
  type ValueFormatterParams,
} from "@/shared/foundation/primitives/Table";

import styles from "./adminTable.module.css";
import { EQUALS_ONLY } from "./adminThreadFilters";
import { filtersToFeedbackFilters } from "./feedbackFilters";

const columnHelper = createColumnHelper<AgentFeedbackRecord>();

const SENTIMENT_OPTIONS = [
  { label: "Positive", value: FeedbackSentiment.POSITIVE },
  { label: "Negative", value: FeedbackSentiment.NEGATIVE },
];

const CATEGORY_OPTIONS = Object.values(FeedbackCategory).map((c) => ({
  label: FEEDBACK_CATEGORY_LABELS[c],
  value: c,
}));

/**
 * Mutable bag the column cell renderers dereference at render time. Stashed in
 * a ref so the column array stays referentially stable (AG Grid treats a fresh
 * column array as a structural reset) — see the matching note on the threads
 * table's ``CellContext``.
 */
interface CellContext {
  selectedFeedbackId: string | undefined;
  onSelect: (feedback: AgentFeedbackRecord) => void;
}

function SentimentCell({
  sentiment,
}: {
  sentiment: FeedbackSentiment;
}): React.ReactNode {
  const theme = useTheme();
  if (sentiment === FeedbackSentiment.POSITIVE) {
    return (
      <ThumbUpAltIcon
        fontSize="inherit"
        sx={{ color: theme.palette.success.main }}
      />
    );
  }
  if (sentiment === FeedbackSentiment.NEGATIVE) {
    return (
      <ThumbDownAltIcon
        fontSize="inherit"
        sx={{ color: theme.palette.error.main }}
      />
    );
  }
  return null;
}

function CategoriesCell({
  categories,
}: {
  categories: AgentFeedbackRecord["categories"];
}): React.ReactNode {
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" alignItems="center">
      {categories.map((c) => (
        <Chip
          key={c}
          label={FEEDBACK_CATEGORY_LABELS[c]}
          size="small"
          variant="outlined"
        />
      ))}
    </Stack>
  );
}

function AdminLabelCell({
  row,
}: {
  row: AgentFeedbackRecord;
}): React.ReactNode {
  // ``admin_label`` when non-empty, else the resolved/open state word. ``||``
  // (not ``??``) so a cleared-to-empty label reverts to the state word.
  // Colour carries the resolved signal so the text never has to say it twice.
  const label = row.admin_label || (row.resolved ? "resolved" : "open");
  return (
    <Chip
      label={label}
      size="small"
      color={row.resolved ? "success" : "warning"}
    />
  );
}

/**
 * Admin feedback columns. Reads live selection/handlers off ``ctxRef`` at
 * render time so the array itself never changes identity. ``When`` doubles as
 * the row's open affordance — DataTable exposes no row-click, so a cell link
 * carries the interaction, the same way the threads table selects via its
 * Thread ID / Title cells.
 */
function buildColumns(
  ctxRef: React.MutableRefObject<CellContext>,
): ColDef<AgentFeedbackRecord>[] {
  return [
    columnHelper.accessor("created", {
      headerName: "When",
      width: 190,
      cellRenderer: (p: ICellRendererParams<AgentFeedbackRecord, string>) => {
        const row = p.data;
        if (!row) {
          return null;
        }
        const selected = ctxRef.current.selectedFeedbackId === row.feedback_id;
        return (
          <button
            type="button"
            className={`${styles.linkButton} ${selected ? styles.linkButtonActive : ""}`}
            onClick={() => ctxRef.current.onSelect(row)}
          >
            {row.created ? new Date(row.created).toLocaleString() : "-"}
          </button>
        );
      },
    }),
    // Not row fields — sentiment/categories/label render bespoke cells, so
    // these carry no accessor (and stay unsortable).
    {
      colId: "sentiment",
      headerName: "Sentiment",
      width: 110,
      sortable: false,
      cellClass: cellStyles.tagsCell,
      cellRenderer: (p: ICellRendererParams<AgentFeedbackRecord>) =>
        p.data ? <SentimentCell sentiment={p.data.sentiment} /> : null,
    },
    {
      colId: "categories",
      headerName: "Categories",
      flex: 1,
      sortable: false,
      cellClass: cellStyles.tagsCell,
      cellRenderer: (p: ICellRendererParams<AgentFeedbackRecord>) =>
        p.data ? <CategoriesCell categories={p.data.categories} /> : null,
    },
    columnHelper.accessor("notes", {
      headerName: "Notes",
      flex: 1,
      tooltipField: "notes",
      valueFormatter: (
        p: ValueFormatterParams<AgentFeedbackRecord, string | null | undefined>,
      ) => p.value || "-",
    }),
    columnHelper.accessor("created_by", { headerName: "User", width: 200 }),
    columnHelper.accessor("org_id", { headerName: "Org", width: 200 }),
    columnHelper.accessor("thread_id", {
      headerName: "Session",
      width: 200,
      cellClass: cellStyles.monoCell,
    }),
    columnHelper.accessor("message_sequence_num", {
      headerName: "Msg #",
      width: 90,
    }),
    {
      colId: "admin_label",
      headerName: "Admin label",
      width: 140,
      sortable: false,
      cellClass: cellStyles.tagsCell,
      cellRenderer: (p: ICellRendererParams<AgentFeedbackRecord>) =>
        p.data ? <AdminLabelCell row={p.data} /> : null,
    },
  ];
}

interface FeedbackTableProps {
  /** ``{ label, value: org_id }`` options for the Org filter dropdown. */
  orgOptions: Array<{ label: string; value: string }>;
  /** ``{ label, value: user_id }`` options for the User filter dropdown. */
  userOptions: Array<{ label: string; value: string }>;
  /**
   * Seeds the Session (``thread_id``) filter at mount — used by deep links
   * (``?sessionId=``/``?feedbackId=``) and the threads table's "show feedback
   * for this thread" hand-off. Captured once; the table owns filter state
   * after that. The parent remounts the view (per view-mode switch) when a new
   * seed needs to apply.
   */
  initialSessionId?: string | undefined;
  onSelectFeedback: (feedback: AgentFeedbackRecord) => void;
  selectedFeedbackId: string | undefined;
}

/**
 * Admin feedback table on the ResourceView/DataTable stack — the structural
 * twin of {@link AgentThreadsTable}. Owns its own filters, pagination, and
 * fetch; selecting a row's ``When`` cell lifts the feedback record to the
 * parent, which renders the triage panel below.
 */
export function FeedbackTable({
  orgOptions,
  userOptions,
  initialSessionId,
  onSelectFeedback,
  selectedFeedbackId,
}: FeedbackTableProps) {
  // Live cell context, refreshed every render so the column array can stay
  // referentially stable (see CellContext).
  const ctxRef = React.useRef<CellContext>({
    selectedFeedbackId,
    onSelect: onSelectFeedback,
  });

  const columns = React.useMemo(() => buildColumns(ctxRef), []);

  const columnOptions = React.useMemo<ColumnPickerOption[]>(
    () =>
      columns.map((c) => ({ id: colId(c), label: c.headerName ?? colId(c) })),
    [columns],
  );

  const filterDefinitions = React.useMemo<FilterDefinition[]>(
    () => [
      {
        type: "enum",
        field: "sentiment",
        name: "Sentiment",
        options: SENTIMENT_OPTIONS,
      },
      {
        type: "enum",
        field: "category",
        name: "Category",
        options: CATEGORY_OPTIONS,
      },
      {
        type: "enum",
        field: "org_id",
        name: "Org",
        options: orgOptions,
      },
      {
        type: "enum",
        field: "created_by",
        name: "User",
        options: userOptions,
      },
      {
        type: "string",
        field: "thread_id",
        name: "Session",
        allowedComparators: EQUALS_ONLY,
      },
      { type: "boolean", field: "resolved", name: "Resolved" },
      { type: "boolean", field: "has_notes", name: "Has notes" },
      { type: "boolean", field: "has_errors", name: "Has errors" },
      { type: "boolean", field: "is_customer", name: "Customer only" },
    ],
    [orgOptions, userOptions],
  );

  // Seed the Session filter from a deep link / cross-view hand-off. Captured
  // once by useResourceView; the view remounts (per view-mode switch) when a
  // different seed needs to take effect.
  const initialActiveFilters = React.useMemo<ActiveFiltersState | undefined>(
    () =>
      initialSessionId
        ? {
            type: "structured",
            matchMode: "AND",
            filters: [
              {
                type: "string",
                field: "thread_id",
                comparator: "EQUALS",
                values: [initialSessionId],
              },
            ],
          }
        : undefined,
    [initialSessionId],
  );

  const view = useResourceView<AgentFeedbackRecord>({
    getRowId: (row) => row.feedback_id,
    defaultVisibleColumnIds: columns.map(colId),
    initialActiveFilters,
  });

  const feedbackFilters = React.useMemo(() => {
    const userFilters: Filter[] =
      view.filters.type === "structured" ? view.filters.filters : [];
    return filtersToFeedbackFilters(userFilters);
  }, [view.filters]);

  // Forward pagination via a `page → after-token` map (the same token-chain
  // idiom as the threads table). Reset when the filters or page size change —
  // the cursors are only valid for the search that produced them (useResourceView
  // already resets currentPage on those transitions).
  const [tokenChain, setTokenChain] = React.useState<Map<number, string>>(
    new Map(),
  );
  React.useEffect(() => {
    setTokenChain(new Map());
  }, [view.filters, view.table.pageSize]);

  const after =
    view.table.currentPage > 0
      ? tokenChain.get(view.table.currentPage)
      : undefined;

  const query = useAdminFeedback({
    ...feedbackFilters,
    pageToken: after,
    limit: view.table.pageSize,
  });

  const nextToken = query.data?.next_token ?? null;
  const currentPage = view.table.currentPage;
  React.useEffect(() => {
    if (nextToken === null) {
      return;
    }
    setTokenChain((prev) => {
      if (prev.get(currentPage + 1) === nextToken) {
        return prev;
      }
      const next = new Map(prev);
      next.set(currentPage + 1, nextToken);
      return next;
    });
  }, [nextToken, currentPage]);

  // Keep the cell-context ref current, then repaint the cells that read it.
  ctxRef.current = { selectedFeedbackId, onSelect: onSelectFeedback };
  const refreshCells = view.table.refreshCells;
  React.useEffect(() => {
    refreshCells();
  }, [refreshCells, selectedFeedbackId]);

  let loading: DataTableLoadingState = "idle";
  if (query.isLoading) {
    loading = "foreground";
  } else if (query.isFetching) {
    loading = "background";
  }

  const rows = query.data?.items ?? [];

  if (query.isError) {
    return (
      <Alert severity="error" className={styles.errorBanner}>
        Failed to load feedback. {query.error?.message ?? ""}
      </Alert>
    );
  }

  return (
    <div className={styles.tableSlot}>
      <ResourceView.Root>
        <ResourceView.Actions>
          <ResourceView.Filters
            filterDefinitions={filterDefinitions}
            onFilterSelected={view.commit}
          />
          <ColumnVisibility
            options={columnOptions}
            visibleColumnIds={view.table.visibleColumnIds}
            setColumnVisibility={view.table.setColumnVisibility}
          />
        </ResourceView.Actions>
        <ResourceView.ActiveFilters
          filters={view.filters}
          filterDefinitions={filterDefinitions}
          onRemove={view.removeFilter}
          onUpdate={view.updateFilter}
          onClear={view.clearFilters}
          onMatchModeChange={view.setMatchMode}
          onFilterSelected={view.commit}
        />
        <DataTable<AgentFeedbackRecord>
          table={view.table}
          hasNextPage={Boolean(query.data?.next_token)}
          loading={loading}
          columns={columns}
          data={rows}
          noRowsContent="No feedback found."
        />
      </ResourceView.Root>
    </div>
  );
}
